import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import HowItWorksModal from '../components/HowItWorksModal';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;600;700&family=Inter:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #080810; color: #F5F5F0; font-family: 'Inter', sans-serif; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes slideUp { from { transform:translateY(100%); } to { transform:translateY(0); } }
  .btn-main { transition: all 0.2s ease; }
  .btn-main:active { transform: scale(0.97); opacity: 0.9; }
  .tab-btn { transition: all 0.2s; }
`;

const C = {
  gold: '#F0B429', goldDark: '#C9820A',
  bg: '#080810', card: '#14141f',
  border: 'rgba(255,255,255,0.08)',
  green: '#06B86A', red: '#E63946',
};

const isNative = !!(window.Capacitor && window.Capacitor.isNative);

function InputField({ label, type, value, onChange, onKeyDown, placeholder, autoFocus, rightEl }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 10, color: '#6B6B7B', letterSpacing: 1, marginBottom: 6, textTransform: 'uppercase' }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input type={type} value={value} onChange={onChange} onKeyDown={onKeyDown}
          placeholder={placeholder} autoFocus={autoFocus}
          style={{ width: '100%', padding: rightEl ? '13px 44px 13px 14px' : '13px 14px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#F5F5F0', fontSize: 15, outline: 'none' }} />
        {rightEl}
      </div>
    </div>
  );
}

function EyeBtn({ show, onToggle }) {
  return (
    <button type="button" onClick={onToggle}
      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#6B6B7B', cursor: 'pointer', fontSize: 18 }}>
      {show ? '🙈' : '👁️'}
    </button>
  );
}

function ErrorBox({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ padding: '10px 14px', background: 'rgba(230,57,70,0.08)', border: '1px solid rgba(230,57,70,0.2)', borderRadius: 10, fontSize: 12, color: '#E63946', marginBottom: 14 }}>
      {msg}
    </div>
  );
}

function Divider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0' }}>
      <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
      <span style={{ fontSize: 11, color: '#555', letterSpacing: 1 }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
    </div>
  );
}

export default function Home() {
  const { t, i18n } = useTranslation();
  
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };
  const navigate = useNavigate();
  const { loginParticipant, registerUser, loginWithGoogle, currentUser } = useAuth();

  // ✅ Red de seguridad: si el usuario ya está autenticado (ej. sesión restaurada
  // después de un reload), redirigir directamente a /mis-grupos
  React.useEffect(() => {
    if (currentUser) {
      navigate('/mis-grupos', { replace: true });
    }
  }, [currentUser, navigate]);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showSheet, setShowSheet] = useState(false);

  // 'login' | 'register' | 'reset'
  const [mode, setMode] = useState('login');

  // Campos compartidos
  const [email, setEmail]       = useState(localStorage.getItem('savedEmail') || '');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);

  // Solo registro
  const [regName, setRegName]           = useState('');
  const [regConfirm, setRegConfirm]     = useState('');

  // Reset
  const [resetSent, setResetSent] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  function openSheet(m = 'login') {
    setMode(m); setError(''); setResetSent(false);
    setPassword(''); setRegConfirm(''); setRegName('');
    setShowSheet(true);
  }

  function closeSheet() {
    setShowSheet(false);
    setError(''); setResetSent(false);
  }

  function switchMode(m) {
    setMode(m); setError(''); setResetSent(false);
    setPassword(''); setRegConfirm('');
  }

  async function handleLogin() {
    if (!email.trim() || !password) return;
    setLoading(true); setError('');
    try {
      await loginParticipant(email.trim(), password);
      localStorage.setItem('savedEmail', email.trim());
      closeSheet();
      navigate('/mis-grupos');
    } catch {
      setError(t('error_wrong_credentials'));
    }
    setLoading(false);
  }

  async function handleGoogle() {
    setLoading(true); setError('');
    try {
      await loginWithGoogle();
      closeSheet();
      // Pequeño delay para que onAuthStateChanged actualice currentUser antes de navegar
      setTimeout(() => navigate('/mis-grupos'), 300);
    } catch (err) {
      const msg = err?.message || err?.code || JSON.stringify(err) || 'Error desconocido';
      setError('Error: ' + msg);
      console.error('Google login error:', err);
      setLoading(false);
    }
  }

  async function handleRegister() {
    if (!regName.trim() || !email.trim() || !password) return;
    if (password.length < 6) { setError(t('error_short_password')); return; }
    if (password !== regConfirm) { setError(t('error_password_mismatch')); return; }
    setLoading(true); setError('');
    try {
      await registerUser(regName.trim(), email.trim(), password);
      localStorage.setItem('savedEmail', email.trim());
      closeSheet();
      navigate('/crear-grupo');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') setError(t('error_email_in_use'));
      else if (err.code === 'auth/invalid-email') setError(t('error_invalid_email'));
      else setError(t('error_create_account'));
    }
    setLoading(false);
  }

  async function handleReset() {
    if (!email.trim()) return;
    setLoading(true); setError('');
    try {
      await sendPasswordResetEmail(getAuth(), email.trim());
      setResetSent(true);
    } catch (err) {
      setError(t('error_email_not_found'));
    }
    setLoading(false);
  }

  const btnGold = {
    width: '100%', padding: '15px',
    background: `linear-gradient(90deg,${C.gold},${C.goldDark})`,
    border: 'none', borderRadius: 14,
    fontFamily: "'Oswald',sans-serif", fontSize: 16, fontWeight: 700,
    letterSpacing: 1, color: '#080810', cursor: 'pointer',
    marginBottom: 10, opacity: loading ? 0.6 : 1,
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column', maxWidth: 480, margin: '0 auto', position: 'relative', overflow: 'hidden' }}>
      <style>{CSS}</style>

      {/* SELECTOR DE IDIOMAS */}
      <div style={{ position: 'absolute', top: 20, right: 16, zIndex: 100, display: 'flex', gap: 4, background: 'rgba(20,20,31,0.9)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: '4px 6px' }}>
        {[['es','ES'],['en','EN'],['pt','PT']].map(([lng, label]) => (
          <button key={lng} onClick={() => changeLanguage(lng)}
            style={{ background: i18n.language?.startsWith(lng) ? C.gold : 'none', border: 'none', borderRadius: 14, cursor: 'pointer', fontSize: 11, fontFamily: "'Oswald',sans-serif", fontWeight: 700, letterSpacing: 1, padding: '4px 10px', color: i18n.language?.startsWith(lng) ? '#080810' : '#6B6B7B', transition: 'all 0.15s' }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ position: 'fixed', top: '-20%', left: '50%', transform: 'translateX(-50%)', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(240,180,41,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 28px 60px', position: 'relative', zIndex: 1 }}>

        {/* Logo */}
        <div style={{ animation: 'fadeUp 0.5s ease both', textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 100, height: 100, borderRadius: 26, margin: '0 auto 20px', background: 'linear-gradient(145deg, #1c1c30, #0d0d1a)', border: '2px solid rgba(240,180,41,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 52, animation: 'float 3s ease-in-out infinite', boxShadow: '0 0 40px rgba(240,180,41,0.15)' }}>⚽</div>
          <div style={{ fontSize: 13, color: C.gold, letterSpacing: 3, marginBottom: 8, fontWeight: 600 }}>
            {t('welcome')}
          </div>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 30, fontWeight: 700, letterSpacing: 4, lineHeight: 1 }}>
            PITAZO <span style={{ color: C.gold }}>MUNDIAL</span>
          </div>
          <div style={{ fontSize: 11, color: '#6B6B7B', letterSpacing: 3, marginTop: 6, maxWidth: 280, margin: '10px auto 0' }}>{t('subtitle')}</div>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 36, animation: 'fadeUp 0.5s ease 0.1s both' }}>
          {[{ num: '104', label: t('stat_matches') }, { num: '48', label: t('stat_teams') }, { num: '3', label: t('stat_countries') }].map((s, i) => (
            <div key={i} style={{ textAlign: 'center', background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '12px 16px' }}>
              <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 22, fontWeight: 700, color: C.gold }}>{s.num}</div>
              <div style={{ fontSize: 9, color: '#6B6B7B', letterSpacing: 1, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12, animation: 'fadeUp 0.5s ease 0.2s both' }}>
          <button className="btn-main" onClick={() => openSheet('login')}
            style={{ width: '100%', padding: '18px', background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`, border: 'none', borderRadius: 16, fontFamily: "'Oswald',sans-serif", fontSize: 18, fontWeight: 700, letterSpacing: 3, color: '#080810', cursor: 'pointer', boxShadow: '0 8px 30px rgba(240,180,41,0.35)' }}>
            ⚽ {t('btn_login')}
          </button>

          <button className="btn-main" onClick={() => setShowHowItWorks(true)}
            style={{ width: '100%', padding: '12px', background: 'transparent', border: 'none', fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#F0B429', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>
            ❓ {t('how_it_works_btn')}
          </button>

          {!isNative && (
            <button className="btn-main" onClick={() => window.open('/descargar.html', '_blank')}
              style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #06B86A, #048a4e)', border: 'none', borderRadius: 14, fontFamily: "'Oswald',sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 2, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 6px 20px rgba(6,184,106,0.3)' }}>
              📲 {t('download_app_btn')}
            </button>
          )}
        </div>

        <div style={{ width: '100%', marginTop: 32, animation: 'fadeUp 0.5s ease 0.3s both' }}>
          <div style={{ fontSize: 10, color: '#444', letterSpacing: 2, textAlign: 'center', marginBottom: 14 }}>{t('how_it_works_label').toUpperCase()}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { icon: '👥', text: t('step_1') },
              { icon: '📲', text: t('step_2') },
              { icon: '⚽', text: t('step_3') },
              { icon: '🏆', text: t('step_4') },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 16px' }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
                <span style={{ fontSize: 13, color: '#aaa', lineHeight: 1.4 }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 24 }}>
          {[{ flag: '🇺🇸', name: 'USA' }, { flag: '🇨🇦', name: 'CAN' }, { flag: '🇲🇽', name: 'MEX' }].map((p, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 26 }}>{p.flag}</div>
              <div style={{ fontSize: 9, color: '#555', letterSpacing: 1, marginTop: 3 }}>{p.name}</div>
            </div>
          ))}
        </div>

        {/* ── SECCIÓN: CÓMO FUNCIONA EXPANDIDA ── */}
        <div style={{ width: '100%', marginTop: 48 }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: 2, color: C.gold, marginBottom: 8 }}>
              ¿CÓMO FUNCIONA?
            </div>
            <div style={{ fontSize: 13, color: '#6B6B7B', lineHeight: 1.6, maxWidth: 400, margin: '0 auto' }}>
              Participar en Pitazo Mundial es muy fácil. Sigue estos 4 pasos y empieza a competir con tus amigos, familia o compañeros de trabajo en la polla más emocionante del Mundial 2026.
            </div>
          </div>

          {[
            {
              icon: '👥',
              title: 'El administrador crea el grupo y elige el plan',
              desc: 'El primer paso es que alguien tome el rol de administrador y cree el grupo de predicciones. Esta persona es la encargada de organizar todo: elige el nombre del grupo, selecciona el plan que mejor se adapta al número de participantes que quiere invitar, y configura las reglas de puntuación según las preferencias del grupo. Pitazo Mundial ofrece desde un plan gratuito para grupos pequeños hasta planes más grandes para empresas o grupos numerosos. El administrador tiene el control total: puede generar links de invitación personalizados, ver las predicciones de todos los participantes, registrar los resultados de cada partido y compartir la tabla de posiciones en tiempo real. Crear un grupo toma menos de 2 minutos.'
            },
            {
              icon: '📲',
              title: 'Invita a tus amigos con un link único',
              desc: 'Una vez creado el grupo, el administrador genera links de invitación personalizados, uno para cada participante. Cada link está vinculado a un nombre específico, lo que garantiza que solo la persona invitada pueda usarlo. El administrador simplemente copia el link y lo comparte por WhatsApp, Telegram, correo electrónico o cualquier otro medio de comunicación. El proceso de registro para el participante es muy sencillo: solo necesita hacer clic en el link, ingresar su correo, crear una contraseña y listo, ya está dentro del grupo. No se necesita descargar ninguna aplicación para participar desde el navegador, aunque también existe la app para Android disponible en Google Play para una experiencia aún más completa y cómoda.'
            },
            {
              icon: '⚽',
              title: 'Predice el marcador de cada partido antes de que empiece',
              desc: 'Este es el corazón de Pitazo Mundial. Antes de que comience cada partido del Mundial 2026, los participantes deben ingresar su predicción del marcador final. Por ejemplo, si crees que Brasil le ganará a Argentina 2-1, ingresas ese marcador. El sistema bloquea automáticamente las predicciones unos minutos antes del inicio de cada partido, asegurando que nadie pueda modificar su predicción una vez que el juego haya comenzado. Puedes hacer tus predicciones con días de anticipación o en el último momento, siempre y cuando sea antes del cierre. La plataforma también envía notificaciones push para recordarte que tienes partidos pendientes de predecir, para que nunca te pierdas una oportunidad de sumar puntos. Con 104 partidos en el torneo, cada predicción cuenta.'
            },
            {
              icon: '🏆',
              title: 'Compite en tiempo real y mira quién va ganando',
              desc: 'Después de cada partido, el administrador registra el resultado oficial y el sistema calcula automáticamente los puntos de todos los participantes según las reglas configuradas. La tabla de posiciones se actualiza en tiempo real y todos pueden verla desde su celular o computador. Los criterios de puntuación son flexibles: se otorgan puntos por acertar el ganador del partido, por acertar la diferencia de goles, por acertar los goles exactos de uno o ambos equipos, y por supuesto, por acertar el marcador exacto, que es la predicción más difícil y la que más puntos otorga. En grupos de planes de pago también se incluyen predicciones especiales antes del inicio del torneo, como el campeón, el subcampeón y los clasificados por grupo, lo que añade una capa extra de emoción y estrategia a la competencia.'
            },
          ].map((step, i) => (
            <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '20px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <span style={{ fontSize: 28, flexShrink: 0 }}>{step.icon}</span>
                <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 15, fontWeight: 600, letterSpacing: 1, color: C.gold, lineHeight: 1.3 }}>{step.title}</div>
              </div>
              <p style={{ fontSize: 13, color: '#aaa', lineHeight: 1.8 }}>{step.desc}</p>
            </div>
          ))}
        </div>

        {/* ── SECCIÓN: QUÉ ES PITAZO MUNDIAL ── */}
        <div style={{ width: '100%', marginTop: 48, background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: '28px 20px' }}>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: 2, color: C.gold, marginBottom: 16, textAlign: 'center' }}>
            ¿QUÉ ES PITAZO MUNDIAL?
          </div>
          <p style={{ fontSize: 13, color: '#aaa', lineHeight: 1.9, marginBottom: 14 }}>
            Pitazo Mundial es una plataforma digital diseñada especialmente para que grupos de amigos, familias, compañeros de trabajo o cualquier comunidad apasionada por el fútbol puedan organizar su propia polla mundialista de manera sencilla, justa y emocionante durante el Mundial 2026, que se celebrará en Estados Unidos, Canadá y México.
          </p>
          <p style={{ fontSize: 13, color: '#aaa', lineHeight: 1.9, marginBottom: 14 }}>
            Una polla mundialista es una competencia entre amigos donde cada participante predice los resultados de los partidos del Mundial antes de que comiencen. A medida que avanza el torneo, se acumulan puntos según la precisión de las predicciones. Al final, quien haya acumulado más puntos se convierte en el campeón del grupo. Es una tradición que existe desde hace décadas en muchos países latinoamericanos, y Pitazo Mundial la trae al mundo digital con todas las herramientas necesarias para administrarla sin esfuerzo.
          </p>
          <p style={{ fontSize: 13, color: '#aaa', lineHeight: 1.9, marginBottom: 14 }}>
            La plataforma está pensada tanto para el organizador como para los participantes. El organizador, llamado administrador, tiene acceso a un panel completo desde donde puede gestionar todo el torneo: crear y administrar el grupo, generar links de invitación personalizados, registrar los resultados de los partidos, configurar las reglas de puntuación y compartir la tabla de posiciones. Los participantes, por su parte, tienen una interfaz limpia y sencilla donde pueden ver los próximos partidos, ingresar sus predicciones y seguir su posición en la tabla en tiempo real.
          </p>
          <p style={{ fontSize: 13, color: '#aaa', lineHeight: 1.9, marginBottom: 14 }}>
            Pitazo Mundial está dirigida a cualquier persona que quiera vivir el Mundial 2026 de una manera más interactiva y competitiva. Desde grupos de WhatsApp de amigos del barrio hasta empresas que quieren hacer una actividad de integración para sus empleados, pasando por familias que se reúnen para ver los partidos juntos. No se necesitan conocimientos técnicos para usarla: si sabes usar WhatsApp, sabes usar Pitazo Mundial.
          </p>
          <p style={{ fontSize: 13, color: '#aaa', lineHeight: 1.9 }}>
            Además, Pitazo Mundial está disponible tanto en versión web, accesible desde cualquier navegador sin necesidad de instalar nada, como en aplicación nativa para Android, disponible en Google Play Store. Esto significa que puedes acceder desde tu computador de trabajo, tu teléfono o la tablet, en cualquier momento y desde cualquier lugar. La plataforma está optimizada para funcionar en Colombia, México, Argentina, Venezuela, Ecuador, Perú y todos los países latinoamericanos donde el fútbol es una pasión.
          </p>
        </div>

        {/* ── SECCIÓN: PREGUNTAS FRECUENTES ── */}
        <div style={{ width: '100%', marginTop: 48, marginBottom: 16 }}>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: 2, color: C.gold, marginBottom: 20, textAlign: 'center' }}>
            PREGUNTAS FRECUENTES
          </div>

          {[
            {
              q: '¿Cómo creo un grupo?',
              a: 'Para crear un grupo primero debes registrarte como administrador haciendo clic en "Ingresar" y creando tu cuenta. Una vez dentro, ve a "Crear nuevo grupo", elige el nombre de tu grupo y selecciona el plan que más se adapte a tu número de participantes. El plan gratuito permite hasta 10 participantes y es perfecto para grupos pequeños. Una vez creado el grupo, ya puedes empezar a generar links de invitación para tus amigos.'
            },
            {
              q: '¿Cómo se calculan los puntos?',
              a: 'El sistema de puntuación es flexible y el administrador puede configurarlo antes de que comience el torneo. En general, se otorgan puntos por acertar el ganador del partido, puntos adicionales por acertar la diferencia de goles, puntos por acertar los goles exactos de uno o ambos equipos, y la mayor cantidad de puntos por acertar el marcador exacto. Por ejemplo, si configuras 1 punto por ganador, 2 por diferencia de goles y 5 por marcador exacto, una predicción de 2-1 cuando el resultado real fue 2-1 te daría los 8 puntos completos.'
            },
            {
              q: '¿Necesito pagar para jugar?',
              a: 'No necesariamente. Pitazo Mundial ofrece un plan gratuito que permite organizar un grupo de hasta 10 participantes con todas las funciones básicas incluidas. Si tu grupo es más grande o quieres acceder a funciones adicionales como predicciones especiales del torneo (campeón, subcampeón, clasificados por grupo), puedes elegir uno de los planes de pago que se adaptan a diferentes tamaños de grupo. El pago lo realiza únicamente el administrador del grupo; los participantes nunca pagan nada para unirse.'
            },
            {
              q: '¿Funciona en celular?',
              a: 'Sí, Pitazo Mundial está completamente optimizada para dispositivos móviles. Puedes usarla directamente desde el navegador de tu celular sin instalar nada, o puedes descargar la aplicación oficial para Android desde Google Play Store para una experiencia nativa más cómoda, con notificaciones push que te recuerdan cuándo tienes partidos pendientes de predecir. La versión iOS estará disponible próximamente.'
            },
            {
              q: '¿Puedo invitar a mis amigos fácilmente?',
              a: 'Sí, el sistema de invitaciones está diseñado para ser lo más sencillo posible. Como administrador, generas un link único para cada participante y simplemente se lo envías por WhatsApp, Telegram o cualquier otro medio. Tu amigo hace clic en el link, se registra en menos de un minuto y queda automáticamente unido a tu grupo. No hay que buscar el grupo manualmente ni ingresar códigos de acceso. Cada link es personal e intransferible, lo que garantiza que solo la persona invitada pueda usarlo.'
            },
            {
              q: '¿Qué pasa si no hago una predicción antes de un partido?',
              a: 'Si no realizas tu predicción antes de que el sistema cierre las apuestas (unos minutos antes del inicio del partido), simplemente no recibirás puntos por ese partido. No hay penalizaciones por no predecir, pero sí pierdes la oportunidad de sumar puntos que podrían ser cruciales para escalar posiciones en la tabla. Por eso la app te envía notificaciones de recordatorio antes del cierre de cada partido, para que nunca te quedes sin predecir por un descuido.'
            },
          ].map((faq, i) => (
            <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '18px', marginBottom: 12 }}>
              <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 14, fontWeight: 600, letterSpacing: 1, color: C.gold, marginBottom: 8 }}>
                ❓ {faq.q}
              </div>
              <p style={{ fontSize: 13, color: '#aaa', lineHeight: 1.8 }}>{faq.a}</p>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 28, fontSize: 10, color: '#333', letterSpacing: 1, textAlign: 'center' }}>
          © 2026 Pitazo Mundial ·{' '}
          <span style={{ color: '#444', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => window.open('https://pitazomundial.com/privacidad.html', '_blank')}>
            {t('privacy')}
          </span>
        </div>
        <div style={{ marginTop: 4, fontSize: 10, color: '#333', letterSpacing: 1, textAlign: 'center' }}>
          Apto para mayores de 13 años · Plataforma de entretenimiento
        </div>
      </div>

      {showHowItWorks && <HowItWorksModal onClose={() => setShowHowItWorks(false)} />}

      {showSheet && (
        <div onClick={closeSheet}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#14141f', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '24px 24px 0 0', padding: '20px 24px 44px', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', animation: 'slideUp 0.28s ease' }}>

            <div style={{ width: 36, height: 4, background: 'rgba(255,255,255,0.15)', borderRadius: 2, margin: '0 auto 20px' }} />

            <div style={{ display: 'flex', background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: 4, marginBottom: 22, gap: 4 }}>
              {[[t('login_tab'), 'login'], [t('register_tab'), 'register']].map(([label, m]) => (
                <button key={m} className="tab-btn" onClick={() => switchMode(m)}
                  style={{ flex: 1, padding: '10px', border: 'none', borderRadius: 9, fontFamily: "'Oswald',sans-serif", fontSize: 13, fontWeight: 600, letterSpacing: 1, cursor: 'pointer', background: mode === m ? `linear-gradient(90deg,${C.gold},${C.goldDark})` : 'transparent', color: mode === m ? '#080810' : '#6B6B7B' }}>
                  {label.toUpperCase()}
                </button>
              ))}
            </div>

            {mode === 'login' && (
              <div>
                <button onClick={handleGoogle} disabled={loading}
                  style={{ width: '100%', padding: 13, background: '#fff', border: 'none', borderRadius: 12, fontFamily: "'Inter',sans-serif", fontSize: 14, fontWeight: 600, color: '#080810', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 4 }}>
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" style={{ width: 18, height: 18 }} />
                  {t('or_enter_with')} GOOGLE
                </button>

                <Divider label={t('or_enter_with').toUpperCase() + " EMAIL"} />

                <InputField label="Email" type="email" value={email} autoFocus
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder={t('email_placeholder')} />

                <InputField label={t('password_placeholder')} type={showPass ? 'text' : 'password'} value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleLogin()}
                  placeholder={t('password_placeholder')}
                  rightEl={<EyeBtn show={showPass} onToggle={() => setShowPass(s => !s)} />} />

                <div style={{ textAlign: 'right', marginTop: -6, marginBottom: 14 }}>
                  <span style={{ fontSize: 11, color: '#F0B429', cursor: 'pointer' }}
                    onClick={() => switchMode('reset')}>
                    🔑 {t('forgot_password')}
                  </span>
                </div>

                <ErrorBox msg={error} />

                <button className="btn-main" onClick={handleLogin} disabled={loading || !email.trim() || !password}
                  style={{ ...btnGold, opacity: (loading || !email.trim() || !password) ? 0.6 : 1 }}>
                  {loading ? '...' : t('btn_login') + ' →'}
                </button>
              </div>
            )}

            {mode === 'register' && (
              <div>
                <button onClick={handleGoogle} disabled={loading}
                  style={{ width: '100%', padding: 13, background: '#fff', border: 'none', borderRadius: 12, fontFamily: "'Inter',sans-serif", fontSize: 14, fontWeight: 600, color: '#080810', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 4 }}>
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" style={{ width: 18, height: 18 }} />
                  {t('btn_register').toUpperCase()} GOOGLE
                </button>

                <Divider label={t('or_enter_with').toUpperCase() + " EMAIL"} />

                <InputField label={t("your_name_label")} type="text" value={regName} autoFocus
                  onChange={e => { setRegName(e.target.value); setError(''); }}
                  placeholder={t("your_name_placeholder")} />

                <InputField label="Email" type="email" value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder={t('email_placeholder')} />

                <InputField label={t('password_placeholder')} type={showPass ? 'text' : 'password'} value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  placeholder={t("min_chars_placeholder")}
                  rightEl={<EyeBtn show={showPass} onToggle={() => setShowPass(s => !s)} />} />

                <InputField label={t("confirm_password_label")} type={showPass ? 'text' : 'password'} value={regConfirm}
                  onChange={e => { setRegConfirm(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleRegister()}
                  placeholder={t("repeat_password_placeholder")}
                  rightEl={<EyeBtn show={showPass} onToggle={() => setShowPass(s => !s)} />} />

                <ErrorBox msg={error} />

                <button className="btn-main" onClick={handleRegister}
                  disabled={loading || !regName.trim() || !email.trim() || !password || !regConfirm}
                  style={{ ...btnGold, opacity: (loading || !regName.trim() || !email.trim() || !password || !regConfirm) ? 0.6 : 1 }}>
                  {loading ? '...' : t('btn_register') + ' →'}
                </button>
              </div>
            )}

            {mode === 'reset' && (
              <div>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>🔑</div>
                  <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, fontWeight: 700, letterSpacing: 2 }}>{t('recover_pass_title')}</div>
                </div>

                {resetSent ? (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                    <button onClick={() => switchMode('login')}
                      style={{ padding: '12px 28px', background: `linear-gradient(90deg,${C.gold},${C.goldDark})`, border: 'none', borderRadius: 12, fontFamily: "'Oswald',sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 1, color: '#080810', cursor: 'pointer' }}>
                      {t('back_to_login').toUpperCase()}
                    </button>
                  </div>
                ) : (
                  <>
                    <InputField label="Email" type="email" value={email} autoFocus
                      onChange={e => { setEmail(e.target.value); setError(''); }}
                      onKeyDown={e => e.key === 'Enter' && handleReset()}
                      placeholder={t('email_placeholder')} />

                    <ErrorBox msg={error} />

                    <button className="btn-main" onClick={handleReset}
                      disabled={loading || !email.trim()}
                      style={{ ...btnGold, opacity: (loading || !email.trim()) ? 0.6 : 1 }}>
                      {loading ? '...' : t('btn_send_link')}
                    </button>

                    <p style={{ textAlign: 'center', fontSize: 11, color: '#555', marginTop: 10 }}>
                      <span style={{ color: C.gold, cursor: 'pointer' }} onClick={() => switchMode('login')}>
                        {t('back_to_login')}
                      </span>
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}